// src/components/recommendations/IlhaLeadsSmartTags.js
// ======================================================
// 🏷️ ILHA LEADS SMART TAGS™ V2 ENTERPRISE
// ======================================================
// 🔥 V2: Animações framer-motion nos tags
// 🔥 V2: Memoização completa — zero re-renders
// 🔥 V2: Validação robusta de context (null-safe)
// 🔥 V2: Suporte a onClick com stopPropagation
// 🔥 V2: Tags com tooltip nativo em dispositivos móveis
// ✅ Etiquetas ultra persuasivas baseadas em dados REAIS
// ✅ Psicologia de urgência, escassez e valor
// ✅ Design premium com gradientes e animações
// ✅ Totalmente white-label
// ======================================================

import React, { useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  Flame, Crown, Sparkles, TrendingUp, TrendingDown,
  Clock, Trophy, Target, Zap, Star, Award, Gem, Eye, Home
} from 'lucide-react';

// ======================================================
// 📊 CONFIGURAÇÃO DAS ETIQUETAS
// ======================================================

export const SMART_TAGS_CONFIG = {
  BEST_MATCH: {
    id: 'best_match',
    condition: (property) => (property?.recommendationScore || 0) >= 85,
    label: '🎯 Seu Melhor Match',
    shortLabel: 'Best Match',
    color: 'from-emerald-500 to-green-500',
    bgColor: 'bg-gradient-to-r from-emerald-500 to-green-500',
    icon: Target,
    priority: 1,
    description: 'Máxima compatibilidade com sua busca'
  },

  HOT_DEAL: {
    id: 'hot_deal',
    condition: (property, context) => {
      if (!property?.price || !context?.avgPrice) return false;
      return Number(property.price) < Number(context.avgPrice) * 0.9;
    },
    label: '🔥 Melhor Preço',
    shortLabel: 'Oferta',
    color: 'from-red-500 to-orange-500',
    bgColor: 'bg-gradient-to-r from-red-500 to-orange-500',
    icon: Flame,
    priority: 2,
    description: 'Preço inferior à média da região'
  },

  LUXURY: {
    id: 'luxury',
    condition: (property, context) => {
      if (!property?.price || !context?.avgPrice) return false;
      return Number(property.price) > Number(context.avgPrice) * 1.5;
    },
    label: '👑 Exclusivo Premium',
    shortLabel: 'Premium',
    color: 'from-amber-400 to-yellow-500',
    bgColor: 'bg-gradient-to-r from-amber-400 to-yellow-500',
    icon: Crown,
    priority: 3,
    description: 'Imóvel de alto padrão'
  },

  NEW_LISTING: {
    id: 'new_listing',
    condition: (property) => {
      const date = property?.publishedAt || property?.createdAt;
      if (!date) return false;
      const daysSince = (Date.now() - new Date(date).getTime()) / (1000 * 60 * 60 * 24);
      return daysSince < 3;
    },
    label: '🆕 Recém Publicado!',
    shortLabel: 'Novo',
    color: 'from-green-500 to-emerald-500',
    bgColor: 'bg-gradient-to-r from-green-500 to-emerald-500',
    icon: Sparkles,
    priority: 4,
    description: 'Publicado nas últimas 72 horas'
  },

  HIGH_DEMAND: {
    id: 'high_demand',
    condition: (property) => (property?.views || 0) >= 15,
    label: '📈 Alta Demanda!',
    shortLabel: 'Popular',
    color: 'from-purple-500 to-pink-500',
    bgColor: 'bg-gradient-to-r from-purple-500 to-pink-500',
    icon: TrendingUp,
    priority: 5,
    description: 'Muitas pessoas interessadas'
  },

  PRICE_DROP: {
    id: 'price_drop',
    condition: (property) => {
      const history = property?.priceHistory || [];
      if (history.length < 2) return false;
      const lastPrice = Number(history[history.length - 1]?.price);
      const previousPrice = Number(history[history.length - 2]?.price);
      return lastPrice > 0 && previousPrice > 0 && lastPrice < previousPrice;
    },
    label: '💚 Preço Reduzido',
    shortLabel: 'Oferta',
    color: 'from-green-400 to-teal-500',
    bgColor: 'bg-gradient-to-r from-green-400 to-teal-500',
    icon: TrendingDown,
    priority: 6,
    description: 'Preço reduzido recentemente'
  },

  LAST_UNITS: {
    id: 'last_units',
    condition: (property, context) => {
      const count = context?.sameTypeCount || 0;
      return count > 0 && count <= 3;
    },
    label: '⏰ Últimas Unidades!',
    shortLabel: 'Escassez',
    color: 'from-red-600 to-rose-600',
    bgColor: 'bg-gradient-to-r from-red-600 to-rose-600',
    icon: Clock,
    priority: 7,
    description: 'Restam poucos imóveis deste tipo'
  },

  SOLD_NEARBY: {
    id: 'sold_nearby',
    condition: (property, context) => context?.hasSoldNearby || false,
    label: '🏆 Vendido na Região',
    shortLabel: 'Sucesso',
    color: 'from-blue-500 to-indigo-500',
    bgColor: 'bg-gradient-to-r from-blue-500 to-indigo-500',
    icon: Trophy,
    priority: 8,
    description: 'Imóveis similares vendidos próximo'
  },

  FEATURED: {
    id: 'featured',
    condition: (property) => property?.featured === true,
    label: '⭐ Destaque',
    shortLabel: 'Destaque',
    color: 'from-yellow-400 to-amber-500',
    bgColor: 'bg-gradient-to-r from-yellow-400 to-amber-500',
    icon: Star,
    priority: 9,
    description: 'Destacado pela imobiliária'
  },

  RENTS_FAST: {
    id: 'rents_fast',
    condition: (property, context) => {
      if (property?.type !== 'Aluguel' && property?.status !== 'Alugado') return false;
      return context?.rentsFastInArea || false;
    },
    label: '⚡ Aluga-se Rápido',
    shortLabel: 'Rápido',
    color: 'from-violet-500 to-purple-500',
    bgColor: 'bg-gradient-to-r from-violet-500 to-purple-500',
    icon: Zap,
    priority: 10,
    description: 'Alta rotatividade de aluguéis na região'
  },

  GEM: {
    id: 'gem',
    condition: (property) => property?.featured && (property?.views || 0) >= 25,
    label: '💎 Joia Imobiliária',
    shortLabel: 'Joia',
    color: 'from-cyan-500 to-blue-500',
    bgColor: 'bg-gradient-to-r from-cyan-500 to-blue-500',
    icon: Gem,
    priority: 11,
    description: 'Altamente recomendado pelo seu valor'
  },

  TRENDING_TODAY: {
    id: 'trending_today',
    condition: (property) => (property?.views || 0) >= 8,
    label: '👁️ Muito Visitado Hoje',
    shortLabel: 'Trending',
    color: 'from-orange-400 to-red-400',
    bgColor: 'bg-gradient-to-r from-orange-400 to-red-400',
    icon: Eye,
    priority: 12,
    description: 'Muitas visitas nas últimas 24h'
  }
};

// ======================================================
// 🏷️ COMPONENTE DE ETIQUETA INDIVIDUAL (MEMOIZADO)
// ======================================================

export const SmartTag = React.memo(({ tag, compact = false, onClick }) => {
  const IconComponent = tag?.icon;
  
  if (!tag || !IconComponent) return null;
  
  const handleClick = useCallback((e) => {
    if (onClick) {
      e?.stopPropagation?.();
      onClick(tag);
    }
  }, [onClick, tag]);
  
  if (compact) {
    return (
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        whileHover={{ scale: 1.15 }}
        className={`${tag.bgColor} text-white p-1.5 rounded-full shadow-md cursor-pointer`}
        title={tag.description}
        onClick={handleClick}
      >
        <IconComponent className="w-3.5 h-3.5" />
      </motion.div>
    );
  }
  
  return (
    <motion.div
      initial={{ scale: 0, opacity: 0, x: -10 }}
      animate={{ scale: 1, opacity: 1, x: 0 }}
      whileHover={{ scale: 1.05 }}
      className={`${tag.bgColor} text-white px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1.5 cursor-pointer select-none`}
      title={tag.description}
      onClick={handleClick}
    >
      <IconComponent className="w-3.5 h-3.5 flex-shrink-0" />
      <span className="text-xs font-bold whitespace-nowrap">{tag.label}</span>
    </motion.div>
  );
});

SmartTag.displayName = 'SmartTag';

// ======================================================
// 🎯 COMPONENTE PRINCIPAL DE ETIQUETAS (MEMOIZADO)
// ======================================================

export const IlhaLeadsSmartTags = React.memo(({ 
  property, 
  context = {}, 
  maxTags = 3,
  compact = false,
  onTagClick
}) => {
  const applicableTags = useMemo(() => {
    if (!property) return [];
    
    return Object.values(SMART_TAGS_CONFIG)
      .filter(tag => {
        try {
          return tag.condition(property, context);
        } catch (error) {
          console.warn(`⚠️ [SmartTags] Erro ao avaliar ${tag.id}:`, error.message);
          return false;
        }
      })
      .sort((a, b) => a.priority - b.priority)
      .slice(0, Math.max(1, maxTags));
  }, [property, context, maxTags]);
  
  if (applicableTags.length === 0) return null;
  
  return (
    <div className={`flex ${compact ? 'gap-1 flex-wrap' : 'flex-col gap-1.5'}`}>
      {applicableTags.map((tag, index) => (
        <SmartTag 
          key={tag.id} 
          tag={tag} 
          compact={compact}
          onClick={onTagClick}
        />
      ))}
    </div>
  );
});

IlhaLeadsSmartTags.displayName = 'IlhaLeadsSmartTags';

// ======================================================
// 🔧 FUNÇÕES AUXILIARES (PURAS, SIN EFECTOS SECUNDARIOS)
// ======================================================

export const getBestTag = (property, context = {}) => {
  if (!property) return null;
  
  const applicable = Object.values(SMART_TAGS_CONFIG)
    .filter(tag => {
      try { return tag.condition(property, context); }
      catch { return false; }
    })
    .sort((a, b) => a.priority - b.priority);
  
  return applicable[0] || null;
};

export const hasTag = (property, tagId, context = {}) => {
  if (!property || !tagId) return false;
  
  const tag = SMART_TAGS_CONFIG[String(tagId).toUpperCase()];
  if (!tag) return false;
  
  try { return tag.condition(property, context); }
  catch { return false; }
};

export const getAllApplicableTags = (property, context = {}) => {
  if (!property) return [];
  
  return Object.values(SMART_TAGS_CONFIG)
    .filter(tag => {
      try { return tag.condition(property, context); }
      catch { return false; }
    })
    .sort((a, b) => a.priority - b.priority);
};

// ======================================================
// 📤 EXPORTS
// ======================================================

export default IlhaLeadsSmartTags;